import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { authApi, setStoredToken, type AuthUser } from '../../lib/api';

type AuthContextValue = {
	isAuthenticated: boolean;
	isReady: boolean;
	needsSetup: boolean;
	user: AuthUser | null;
	login: (email: string, password: string) => Promise<boolean>;
	logout: () => Promise<void>;
	refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
	const [isReady, setIsReady] = useState(false);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [needsSetup, setNeedsSetup] = useState(false);
	const [user, setUser] = useState<AuthUser | null>(null);

	const refresh = useCallback(async () => {
		try {
			const setup = await authApi.checkSetup();
			setNeedsSetup(Boolean(setup.needsSetup));
			if (setup.needsSetup) {
				setUser(null);
				setIsAuthenticated(false);
				setIsReady(true);
				return;
			}

			const me = await authApi.me();
			setUser(me);
			setIsAuthenticated(true);
		} catch {
			setUser(null);
			setIsAuthenticated(false);
		} finally {
			setIsReady(true);
		}
	}, []);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	const login = useCallback(async (email: string, password: string) => {
		try {
			const result = await authApi.login(email, password);
			if (result.token) {
				setStoredToken(result.token);
			}
			setUser(result.user ?? null);
			setIsAuthenticated(true);
			toast.success('Welcome back');
			return true;
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Sign in failed');
			return false;
		}
	}, []);

	const logout = useCallback(async () => {
		try {
			await authApi.logout();
		} catch {
			undefined;
		} finally {
			setStoredToken(null);
			setUser(null);
			setIsAuthenticated(false);
		}
	}, []);

	const value = useMemo(
		() => ({ isAuthenticated, isReady, needsSetup, user, login, logout, refresh }),
		[isAuthenticated, isReady, needsSetup, user, login, logout, refresh]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within AuthProvider');
	}
	return context;
}
